import { createContext, ReactNode, useContext, useMemo } from 'react';

import { csrClient } from '../apollo/api/avantArteClient';
import { storefrontClient } from '../apollo/shopify/storefrontClient';
import AvantArteAuthGateway from '../gateways/AvantArteAuthGateway';
import { AvantArteProductGateway } from '../gateways/AvantArteProductGateway';
import { ShopifyCheckoutGateway } from '../gateways/ShopifyCheckoutGateway';
import { ShopifyProductsGateway } from '../gateways/ShopifyProductsGateway';
import { ShopifyUnlockGateway } from '../gateways/ShopifyUnlockGateway';
import { useProductPurchasing } from '../hooks/useProductPurchasing';
import { useReleaseGating } from '../hooks/useReleaseGating';
import { useReleaseName } from '../hooks/useReleaseName';
import {
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../types/Purchasing';
import { ProductSourceBackend } from './ProductProvider';

const authGateway = new AvantArteAuthGateway(csrClient, storefrontClient);
const aaProductsGateway = new AvantArteProductGateway(csrClient);
const checkoutGateway = new ShopifyCheckoutGateway(storefrontClient);
const productsGateway = new ShopifyProductsGateway(storefrontClient);
const unlockGateway = new ShopifyUnlockGateway();

type PurchaseContextType =
  ProductPurchasingController<ProductPurchaseDetails> & {
    backendProduct: ProductSourceBackend | null;
  };

interface PurchaseContextProps {
  backendProduct?: ProductSourceBackend | null;
  children: ReactNode;
  product: ProductType | null;
  productDetails: ProductPurchaseDetails | null;
}

const PurchaseContext = createContext<PurchaseContextType>({
  backendProduct: null,
  loading: true,
  checkoutLoading: false,
  dispatchDate: { isPast: false, range: null, weeks: null },
  product: null,
  frame: null,
  frameOptions: null,
  unlocked: false,
  unlockLoading: false,
  unlockMessage: undefined,
  unlockCode: undefined,
  requiresUnlock: false,
  quantity: 1,
  release: undefined,
  releaseGating: undefined,
  requiresGating: false,
  canPurchase: true,
  setFrame: () => {},
  goToCheckout: () => Promise.resolve(),
  unlockProduct: async () => {},
  setQuantity: () => {},
  setUnlockCode: () => {},
  collectionSetToggleId: () => {},
  collectionSetToggleAll: () => {},
  collectionSetUpdateQuantity: () => {},
  collectionSet: null,
  collectionSetDescription: null,
  collectionSetImage: null,
  isFramedByDefault: false,
  amlCheckNeeded: false,
  isPreSelectedCollectionSet: false,
  setIsPreSelectedCollectionSet: () => {},
});

export const PurchaseProvider = ({
  backendProduct = null,
  children,
  product,
  productDetails,
}: PurchaseContextProps) => {
  const identifier = product?.id?.[0] as ShopifyProductIdentifier;
  const release = useReleaseName(product || undefined);
  const releaseGating = useReleaseGating({ product: product || undefined });
  const releaseType = product?.releaseType;

  const state = useProductPurchasing({
    identifier,
    release,
    releaseGating,
    releaseType,
    productDetails,
    authGateway,
    aaProductsGateway,
    checkoutGateway,
    productsGateway,
    unlockGateway,
  });

  const context = useMemo(
    () => ({ ...state, backendProduct }),
    [backendProduct, state],
  );

  return (
    <PurchaseContext.Provider value={context}>
      {children}
    </PurchaseContext.Provider>
  );
};

export const usePurchase = () => useContext(PurchaseContext);
